import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChildren,
    QueryList,
    ElementRef,
} from "@angular/core";
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { Dataset } from "../../model/dataset/dataset";
import { DatasetField } from "../../model/dataset/field/dataset-field";
import { RenderingOptions } from "../../model/dataset/rendering/rendering-options";
import {
    ClimatologyOptions,
    ClimateIndicator,
    TimePeriod,
    ScenarioSSP,
    getDefaultClimatologyOptions,
} from "../../model/dataset/rendering/climatology-options";
import { ColorScaleType } from "../../model/dataset/rendering/color-scale-type";
import { DatasetStylingOptions } from "../../model/dataset/rendering/dataset-styling-options";
import { ColorUtils } from "../../core/utils/color-utils";
import { RenderingUtils } from "../../core/utils/rendering-utils";
import { NotifService } from "../../core/notification/notif.service";
import { DatasetFieldType } from "../../model/dataset/dataset-field-type";
import { DatasetFieldSpecificType } from "../../model/dataset/dataset-field-specific.type";
import { IntervalOption } from "../../model/dataset/rendering/interval-option";
import { DatapointsAggregateService } from "../../data-access-layer/datapoints/datapoints-aggregate.service";
import { DatapointFilter } from "../../model/datapoint/filter/datapoint-filter";
import { checkIntervalsValidity } from "../validators/colorization/colorization.validator";
import { RenderingErrorCodes } from "../../model/dataset/rendering/rendering-error-codes";
import { DatapointConverterType } from "../../model/dataset/rendering/datapoint-converter-options";
import { VisualizationType } from "../../model/dataset/rendering/visualization-options";
import { OverlaysService } from "../../data-access-layer/global-overlays/overlays.service";
import { CLIMATE_INDICATORS } from "./climate-indicators.constants";
import {
    SCORE_TYPE,
    ScoreType,
    CONSOLIDATED_RISK_SCORE_COLORS,
    CONSOLIDATED_RISK_SCORE_DEFAULT_COLOR,
} from "./colorize-markers.constants";
import { Subscription } from "rxjs";
import { ActivityConfigService } from "../../account/consolidated-risk-score/services/activity-config.service";
import { Activity } from "../../account/consolidated-risk-score/model/activity-config.model";

@Component({
    selector: "mpt-colorize-markers",
    templateUrl: "./colorize-markers.component.html",
    styleUrls: ["./colorize-markers.component.scss"],
})
export class ColorizeMarkersComponent implements OnInit {
    @Input() dataset: Dataset;
    @Input() renderingOptions: RenderingOptions;

    @Output() submitChanges = new EventEmitter<RenderingOptions>();
    @Output() Reset = new EventEmitter<void>();

    @ViewChildren("colorInputs") colorInputs: QueryList<ElementRef>;
    @Output() closePanel = new EventEmitter<void>();

    colorizationForm: UntypedFormGroup;
    intervalForm: UntypedFormGroup;

    // Enums for template
    ClimateIndicator = ClimateIndicator;
    TimePeriod = TimePeriod;
    ScenarioSSP = ScenarioSSP;
    ColorScaleType = ColorScaleType;
    ColorUtils = ColorUtils;
    DatasetFieldSpecificType = DatasetFieldSpecificType;

    // Dropdown options - populated from dataset's climatology overlay config
    climateIndicators: Array<{ field: DatasetField; label: string }> = [];

    timePeriods = [
        { value: TimePeriod.HISTORIC, label: "Historical (1995–2014)" },
        { value: TimePeriod.NEAR, label: "Near Term (2021-2040)" },
        { value: TimePeriod.MID, label: "Mid Term (2041-2060)" },
        { value: TimePeriod.LONG, label: "Long Term (2081-2100)" },
    ];

    scenarios = [
        { value: ScenarioSSP.SSP119, label: "SSP1-1.9" },
        { value: ScenarioSSP.SSP126, label: "SSP1-2.6" },
        { value: ScenarioSSP.SSP245, label: "SSP2-4.5" },
        { value: ScenarioSSP.SSP370, label: "SSP3-7.0" },
        { value: ScenarioSSP.SSP585, label: "SSP5-8.5" },
    ];

    /**
     * Update time period and scenario dropdowns based on the selected indicator.
     * For climate_slr_v2, hide Historical and SSP1-1.9.
     */
    private updateDropdownsForIndicator(indicatorName: string): void {
        if (this.OVERLAYS_WITHOUT_HISTORICAL_SSP119.includes(indicatorName)) {
            this.timePeriods = this.allTimePeriods.filter(
                (tp) => tp.value !== TimePeriod.HISTORIC,
            );
            this.scenarios = this.allScenarios.filter(
                (s) => s.value !== ScenarioSSP.SSP119,
            );

            // Reset form values if they are now invalid
            const timePeriodControl = this.colorizationForm?.get("timePeriod");
            const scenarioControl = this.colorizationForm?.get("scenario");

            if (timePeriodControl?.value === TimePeriod.HISTORIC) {
                timePeriodControl.setValue(TimePeriod.NEAR);
            }
            if (scenarioControl?.value === ScenarioSSP.SSP119) {
                scenarioControl.setValue(ScenarioSSP.SSP126);
            }
        } else {
            this.timePeriods = [...this.allTimePeriods];
            this.scenarios = [...this.allScenarios];
        }
    }

    scaleTypes = [
        ColorScaleType.FIXED,
        // ColorScaleType.GRADIENT,
        // ColorScaleType.INTERVAL,
        // ColorScaleType.CONSTANT
    ];

    // Color management
    base64Colors: string[] = [];
    defaultColor = "#d3d3d3";
    colorInterval = "#aaaaaa";

    // Fields for colorization
    numericFields: DatasetField[] = [];
    selectedFieldForColorization: DatasetField | null = null;

    // Special colorization options
    specialColorOptions = [
        { value: SCORE_TYPE.RISK_SCORE, label: "Climate Score" },
    ];

    // Exposed for the template - it can only resolve members of the component instance
    readonly scoreType = SCORE_TYPE;

    // Score type toggle
    selectedScoreType: ScoreType = SCORE_TYPE.RISK_SCORE;
    selectedConsolidatedScoreType: typeof SCORE_TYPE.CONSOLIDATED_RISK_SCORE =
        SCORE_TYPE.CONSOLIDATED_RISK_SCORE;

    // Consolidated risk score - vulnerability profiles
    vulnerabilityProfiles: Activity[] = [];
    // Either a profile type or the "All" sentinel, which is offered as the first value of the same
    // dropdown: colorize by the score of whichever activity each location belongs to, instead of
    // one chosen profile type. A location has exactly one activity, so nothing is ambiguous.
    selectedVulnerabilityProfile: Activity | string | null = null;
    static readonly ALL_VULNERABILITY_PROFILES = "ALL";
    static readonly ALL_VULNERABILITY_PROFILES_LABEL = "All";
    readonly allProfilesOption =
        ColorizeMarkersComponent.ALL_VULNERABILITY_PROFILES;
    readonly allProfilesLabel =
        ColorizeMarkersComponent.ALL_VULNERABILITY_PROFILES_LABEL;

    get allVulnerabilityProfiles(): boolean {
        return (
            this.selectedVulnerabilityProfile ===
            ColorizeMarkersComponent.ALL_VULNERABILITY_PROFILES
        );
    }
    // Overlays that should not show Historical time period and SSP1-1.9 scenario
    private readonly OVERLAYS_WITHOUT_HISTORICAL_SSP119 = ["climate_slr_v2"];
    private readonly OVERLAYS_WITHOUT_HISTORICAL = ["climate_slr_v2"];

    // Full (unfiltered) option lists
    private readonly allTimePeriods = [
        { value: TimePeriod.HISTORIC, label: "Historical (1995–2014)" },
        { value: TimePeriod.NEAR, label: "Near Term (2021-2040)" },
        { value: TimePeriod.MID, label: "Mid Term (2041-2060)" },
        { value: TimePeriod.LONG, label: "Long Term (2081-2100)" },
    ];

    private readonly allScenarios = [
        { value: ScenarioSSP.SSP119, label: "SSP1-1.9" },
        { value: ScenarioSSP.SSP126, label: "SSP1-2.6" },
        { value: ScenarioSSP.SSP245, label: "SSP2-4.5" },
        { value: ScenarioSSP.SSP370, label: "SSP3-7.0" },
        { value: ScenarioSSP.SSP585, label: "SSP5-8.5" },
    ];

    // Subscription holder to avoid duplicate valueChanges listeners
    private timePeriodSub: Subscription | null = null;
    private indicatorSub: Subscription | null = null;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly notifService: NotifService,
        private readonly datapointAggregateService: DatapointsAggregateService,
        private readonly overlaysService: OverlaysService,
        private readonly activityConfigService: ActivityConfigService,
    ) {
        this.intervalForm = this.formBuilder.group({
            color: ["#aaaaaa", [Validators.required]],
            minValue: [null, [Validators.required]],
            maxValue: [null],
        });
    }

    ngOnInit(): void {
        this.initializeClimatologyStylingOptions();
        this.cleanupCorruptedIntervals();
        this.loadClimateIndicatorsFromDataset();
        this.loadVulnerabilityProfiles();
        this.initializeForm();
        this.loadNumericFields();
        this.populateColorsFromClimatologyStylingOptions();
        this.syncIntervalFormState();
        this.setupTimePeriodChangeListener();
    }

    private cleanupCorruptedIntervals(): void {
        // Remove any point intervals [n, n] that may have been loaded from storage
        const intervals =
            this.dataset.climatologyStylingOptions?.intervalOptions;
        if (!intervals || intervals.length === 0) {
            return;
        }

        for (let i = intervals.length - 1; i >= 0; i--) {
            const interval = intervals[i];
            if (interval.minValue != null && interval.maxValue != null) {
                const min = Number(interval.minValue);
                const max = Number(interval.maxValue);
                if (min === max) {
                    console.log(
                        `Cleaning up corrupted point interval at index ${i}: [${min}, ${max}]`,
                    );
                    intervals.splice(i, 1);
                }
            }
        }
    }

    private syncIntervalFormState(): void {
        // Sync interval form based on existing intervals
        const intervals =
            this.dataset.climatologyStylingOptions?.intervalOptions;
        if (intervals && intervals.length > 0) {
            const lastInterval = intervals[intervals.length - 1];
            if (lastInterval.maxValue !== null) {
                this.intervalForm.controls.minValue.setValue(
                    lastInterval.maxValue,
                );
                this.intervalForm.controls.minValue.disable();
            }
        }
        // Sync color display
        const formColor = this.intervalForm.controls.color.value;
        if (formColor) {
            this.colorInterval = formColor;
        }
    }

    private loadClimateIndicatorsFromDataset(): void {
        // Fetch overlays from API and match with climate indicators
        if (!this.dataset?.accountID) {
            this.climateIndicators = [];
            return;
        }

        this.overlaysService
            .getAllAccountOverlays(this.dataset.accountID)
            .subscribe(
                (overlays: Dataset[]) => {
                    // Match overlays with CLIMATE_INDICATORS_LEGEND based on dataset ID (acronym)
                    this.climateIndicators = overlays
                        .filter((overlay) => {
                            // Check if overlay's dataset ID matches any climate indicator acronym
                            const overlayId = overlay.id?.toLowerCase();
                            return overlayId && CLIMATE_INDICATORS[overlayId];
                        })
                        .map((overlay) => {
                            // Create a field-like object for the matched overlay
                            const acronym = overlay.id.toLowerCase();
                            const indicator = CLIMATE_INDICATORS[acronym];

                            // Use the first field from the overlay or create a synthetic field
                            const field =
                                overlay.fields && overlay.fields.length > 0
                                    ? overlay.fields[0]
                                    : ({
                                          id: overlay.id,
                                          name: indicator.name,
                                          displayName: indicator.name,
                                          type: DatasetFieldSpecificType.NUMBER_FIELD,
                                      } as DatasetField);

                            return {
                                field: {
                                    ...field,
                                    name: acronym,
                                    displayName: indicator.name,
                                },
                                label: indicator.name,
                            };
                        })
                        .sort((a, b) => a.label.localeCompare(b.label));
                },
                (error) => {
                    console.error(
                        "Error fetching overlays for climate indicators:",
                        error,
                    );
                    this.notifService.error(
                        "Failed to load climate indicators",
                    );
                    this.climateIndicators = [];
                },
            );
    }

    private loadVulnerabilityProfiles(): void {
        // Fetch vulnerability profiles from API
        this.activityConfigService.getActivities().subscribe(
            (profiles: Activity[]) => {
                this.vulnerabilityProfiles = profiles.sort((a, b) =>
                    a.name.localeCompare(b.name),
                );
            },
            (error) => {
                console.error("Error fetching vulnerability profiles:", error);
                this.notifService.error(
                    "Failed to load vulnerability profiles",
                );
                this.vulnerabilityProfiles = [];
            },
        );
    }

    private initializeClimatologyStylingOptions(): void {
        // Initialize climatologyStylingOptions if not present
        if (!this.dataset.climatologyStylingOptions) {
            this.dataset.climatologyStylingOptions = {
                type: ColorScaleType.GRADIENT,
                colorizationFieldIndex: null, // null means no field selected yet
                defaultColor: 0xd3d3d3,
                colors: [0xfff2cc, 0xffd966, 0xf4cccc, 0xe06666, 0xcc0000], //[0x2166ac, 0x92c5de, 0xfddbc7, 0xf4a582, 0xb2182b],
                backgroundOpacity: 1,
                intervalOptions: [],
            };
        }
    }

    private initializeForm(): void {
        // Initialize climatology options if not present
        if (!this.renderingOptions.climatologyOptions) {
            this.renderingOptions.climatologyOptions =
                getDefaultClimatologyOptions();
        }

        // Find the matching field for the selected indicator
        const selectedIndicatorName =
            this.renderingOptions.climatologyOptions.selectedIndicator;
        const selectedIndicatorField =
            this.climateIndicators.find(
                (ind) =>
                    ind.field.name === selectedIndicatorName ||
                    ind.field.displayName === selectedIndicatorName,
            )?.field || null;

        // Get the previously selected colorization field
        // Default to null to force user selection (no auto-selection)
        let previouslySelectedField = null;
        const colorizationFieldIndex =
            this.dataset.climatologyStylingOptions?.colorizationFieldIndex;

        // Only load previous selection if explicitly saved (not on first load)
        if (
            colorizationFieldIndex !== null &&
            colorizationFieldIndex !== undefined &&
            colorizationFieldIndex >= 0 &&
            this.dataset.fields &&
            this.dataset.fields[colorizationFieldIndex]
        ) {
            previouslySelectedField =
                this.dataset.fields[colorizationFieldIndex];
            this.selectedFieldForColorization = previouslySelectedField;
        } else if (
            this.renderingOptions.converterOptions?.fieldID &&
            this.dataset.climatologyStylingOptions?.colorizationFieldIndex !==
                null
        ) {
            // Only restore if there was a previous explicit selection
            const fieldId = this.renderingOptions.converterOptions.fieldID;
            if (
                fieldId === SCORE_TYPE.RISK_SCORE ||
                fieldId === SCORE_TYPE.RISK_LEVEL
            ) {
                previouslySelectedField = fieldId;
            } else {
                previouslySelectedField =
                    this.dataset.fields.find((f) => f.id === fieldId) || null;
                this.selectedFieldForColorization = previouslySelectedField;
            }
        }
        // If no previous selection, leave as null to force user to choose

        // Determine if scenario is required based on time period
        const isHistoric =
            this.renderingOptions.climatologyOptions.timePeriod ===
            TimePeriod.HISTORIC;
        const scenarioValidators = isHistoric ? [] : [Validators.required];

        this.colorizationForm = this.formBuilder.group({
            indicator: [selectedIndicatorField, Validators.required],
            timePeriod: [
                this.renderingOptions.climatologyOptions.timePeriod,
                Validators.required,
            ],
            scenario: [
                {
                    value: this.renderingOptions.climatologyOptions.scenario,
                    disabled: isHistoric,
                },
                scenarioValidators,
            ],
            colorizationField: [previouslySelectedField, Validators.required],
            colorScaleType: [null, Validators.required], // No default - user must select
        });

        // Ensure listeners are wired to the freshly created form controls
        this.setupTimePeriodChangeListener();
        this.setupIndicatorChangeListener();
    }

    /**
     * Setup listener for time period changes to enable/disable scenario dropdown
     */
    private setupTimePeriodChangeListener(): void {
        // Prevent duplicate subscriptions when form is recreated (e.g., Cancel/Reset)
        if (this.timePeriodSub) {
            this.timePeriodSub.unsubscribe();
            this.timePeriodSub = null;
        }

        this.timePeriodSub = this.colorizationForm
            .get("timePeriod")
            .valueChanges.subscribe((timePeriod: TimePeriod) => {
                const scenarioControl = this.colorizationForm.get("scenario");

                if (timePeriod === TimePeriod.HISTORIC) {
                    // Disable scenario dropdown for Historic period
                    scenarioControl.disable();
                    scenarioControl.clearValidators();
                    scenarioControl.setValue(null);
                } else {
                    // Enable scenario dropdown for other periods
                    scenarioControl.enable();
                    scenarioControl.setValidators([Validators.required]);

                    // Set default scenario if none selected
                    if (!scenarioControl.value) {
                        const defaultScenario = this.scenarios.some(
                            (s) => s.value === ScenarioSSP.SSP119,
                        )
                            ? ScenarioSSP.SSP119
                            : ScenarioSSP.SSP126;
                        scenarioControl.setValue(defaultScenario);
                    }
                }

                scenarioControl.updateValueAndValidity();
            });
    }

    /**
     * Setup listener for indicator changes to filter time period and scenario dropdowns
     */
    private setupIndicatorChangeListener(): void {
        if (this.indicatorSub) {
            this.indicatorSub.unsubscribe();
            this.indicatorSub = null;
        }

        // Apply filtering for the initial indicator value
        const initialIndicator = this.colorizationForm.get("indicator")?.value;
        if (initialIndicator?.name) {
            this.updateDropdownsForIndicator(initialIndicator.name);
        }

        this.indicatorSub = this.colorizationForm
            .get("indicator")
            .valueChanges.subscribe((field: any) => {
                const indicatorName = field?.name || "";
                this.updateDropdownsForIndicator(indicatorName);
            });
    }

    private loadNumericFields(): void {
        if (this.dataset && this.dataset.fields) {
            this.numericFields = this.dataset.fields.filter(
                (field) =>
                    field.specificType ===
                    DatasetFieldSpecificType.NUMBER_FIELD,
            );
        }
    }

    private populateColorsFromClimatologyStylingOptions(): void {
        const climatologyStyling = this.dataset.climatologyStylingOptions;

        // Load colors from climatologyStylingOptions
        if (
            !climatologyStyling.colors ||
            climatologyStyling.colors.length === 0
        ) {
            // Default climatology color gradient (blue to red for temperature/precipitation)
            this.base64Colors = [
                "#fff2cc",
                "#ffd966",
                "#f4cccc",
                "#e06666",
                "#cc0000",
            ]; //['#2166ac', '#92c5de', '#fddbc7', '#f4a582', '#b2182b'];
        } else {
            // Load saved colors
            this.base64Colors = climatologyStyling.colors.map((color) =>
                ColorUtils.colorToBase64(color),
            );
        }

        // Load default/constant color
        if (climatologyStyling.defaultColor) {
            this.defaultColor = ColorUtils.colorToBase64(
                climatologyStyling.defaultColor,
            );
        } else {
            this.defaultColor = "#d3d3d3";
        }
    }

    onFieldSelected(field: DatasetField): void {
        if (!this.dataset) {
            return;
        }

        this.selectedFieldForColorization = field;
        this.renderingOptions.converterOptions.fieldID = field.id;
        this.renderingOptions.converterOptions.datasetID = this.dataset.id;
        this.dataset.climatologyStylingOptions.colorizationFieldIndex =
            this.dataset.fields.indexOf(field);
        this.checkScaleTypeByStatisticalValues();
    }

    onSpecialOptionSelected(optionValue: string): void {
        // Handle special colorization options (Risk Score, Risk Level)
        if (!this.dataset) {
            return;
        }

        this.selectedFieldForColorization = null; // Clear regular field selection

        // Store the special option type in converter options
        if (this.renderingOptions.converterOptions) {
            this.renderingOptions.converterOptions.fieldID = optionValue; // 'risk_score' or 'risk_level'
            this.renderingOptions.converterOptions.datasetID = this.dataset.id;
        }

        // Set a special index to indicate it's not a regular field
        this.renderingOptions.datasetStylingOptions.colorizationFieldIndex = -1;
    }

    onColorScaleTypeChanged(newType: ColorScaleType): void {
        if (!this.dataset) {
            return;
        }

        this.dataset.climatologyStylingOptions.type = newType;

        // Clear intervals when switching away from INTERVAL type
        if (newType !== ColorScaleType.INTERVAL) {
            this.dataset.climatologyStylingOptions.intervalOptions = [];
        }

        if (newType !== ColorScaleType.CONSTANT) {
            if (this.base64Colors.length === 0) {
                this.base64Colors = ColorUtils.defaultColorScale();
            }
        }
        this.checkScaleTypeByStatisticalValues();
    }

    onColorChange(newColor: string, index: number): void {
        this.base64Colors[index] = newColor;
    }

    onConstantColorChanged(newColor: string): void {
        if (!this.dataset) {
            return;
        }

        this.dataset.climatologyStylingOptions.defaultColor =
            ColorUtils.colorFromBase64(newColor);
        this.defaultColor = newColor;
    }

    addColor(): void {
        this.base64Colors.push("#000000");
        this.checkScaleTypeByStatisticalValues();
    }

    removeColor(index: number, event: Event): void {
        event.stopPropagation();
        if (this.base64Colors.length > 1) {
            this.base64Colors.splice(index, 1);
        }
        this.checkScaleTypeByStatisticalValues();
    }

    openColorPicker(index: number): void {
        const inputs = this.colorInputs.toArray();
        if (inputs && inputs[index]) {
            inputs[index].nativeElement.click();
        }
    }

    onApply(): void {
        // Mark all fields as touched to show validation errors
        Object.keys(this.colorizationForm.controls).forEach((key) => {
            this.colorizationForm.controls[key].markAsTouched();
        });

        // Basic form validation
        if (!this.colorizationForm.valid) {
            this.notifService.error("Please fill all required fields");
            return;
        }

        const formValue = this.colorizationForm.value;
        const colorizationFieldValue = formValue.colorizationField;
        // VALIDATION 1: Colorized By field must be selected
        if (!colorizationFieldValue) {
            this.notifService.error('Please select a field for "Colorized By"');
            this.colorizationForm.controls.colorizationField.markAsTouched();
            return;
        }

        const isSpecialOption =
            typeof colorizationFieldValue === "string" &&
            (colorizationFieldValue === SCORE_TYPE.RISK_SCORE ||
                colorizationFieldValue === SCORE_TYPE.RISK_LEVEL);

        if (!this.selectedFieldForColorization && !isSpecialOption) {
            this.notifService.error(
                "Please select a valid field for colorization",
            );
            return;
        }

        // VALIDATION 2: For INTERVAL type, at least 1 interval MUST be defined
        const colorScaleType = this.dataset?.climatologyStylingOptions?.type;

        if (colorScaleType === ColorScaleType.INTERVAL) {
            const minValue = this.intervalForm.controls.minValue.value;
            const maxValue = this.intervalForm.controls.maxValue.value;
            const existingIntervals =
                this.dataset.climatologyStylingOptions.intervalOptions || [];

            // Auto-add pending interval if valid
            if (
                minValue !== null &&
                maxValue !== null &&
                this.intervalForm.valid
            ) {
                this.addInterval();
            }

            // After auto-add, check if we have at least one interval
            const totalIntervals = (
                this.dataset.climatologyStylingOptions.intervalOptions || []
            ).length;

            if (totalIntervals === 0) {
                this.notifService.error(
                    "For INTERVAL colorization type, you must define at least 1 interval",
                );
                return;
            }
        }

        // Use existing RenderingUtils validation system
        if (this.dataset && this.selectedFieldForColorization) {
            // Prepare rendering options for validation
            const tempRenderingOptions: RenderingOptions = {
                datasetStylingOptions: this.dataset.climatologyStylingOptions,
                converterOptions: {
                    type: this.getConverterType(
                        this.selectedFieldForColorization,
                    ),
                    datasetID: this.dataset.id,
                    fieldID: this.selectedFieldForColorization.id,
                },
                visualizationOptions: { type: VisualizationType.DEFAULT },
            };

            // Use RenderingUtils to validate
            const invalidAttribute =
                RenderingUtils.validate(tempRenderingOptions);

            if (invalidAttribute) {
                // Use existing error message mapping
                switch (invalidAttribute) {
                    case RenderingErrorCodes.COLORS:
                        this.notifService.error(
                            "You must select some colors for the colorization",
                        );
                        break;
                    case RenderingErrorCodes.FIELD:
                        this.notifService.error(
                            "You must choose a field for the colorization",
                        );
                        break;
                    case RenderingErrorCodes.INTERVALS:
                        this.notifService.error(
                            "You must define at least one interval",
                        );
                        break;
                    case RenderingErrorCodes.INTERVALS_OVERLAP:
                        this.notifService.error(
                            "The defined intervals must not overlap",
                        );
                        break;
                    case RenderingErrorCodes.DUPLICATE_INTERVAL:
                        this.notifService.error(
                            "The defined intervals must not have duplicates",
                        );
                        break;
                    default:
                        this.notifService.error(
                            "Please check your colorization settings",
                        );
                }
                return;
            }
        }

        // Update climatology options from form
        const selectedIndicatorField = formValue.indicator as DatasetField;
        const selectedTimePeriod = formValue.timePeriod;

        // For Historic period, pass null/blank for scenario
        const selectedScenario =
            selectedTimePeriod === TimePeriod.HISTORIC
                ? null
                : formValue.scenario ||
                  this.colorizationForm.get("scenario").value;

        this.renderingOptions.climatologyOptions = {
            enabled: true,
            selectedIndicator:
                selectedIndicatorField?.name ||
                selectedIndicatorField?.displayName ||
                "",
            timePeriod: selectedTimePeriod,
            scenario: selectedScenario,
        };

        // Update climatology styling options
        if (this.dataset?.climatologyStylingOptions) {
            this.dataset.climatologyStylingOptions.colors =
                this.base64Colors.map((color) =>
                    ColorUtils.colorFromBase64(color),
                );

            // Ensure intervalOptions are cleared for non-INTERVAL types
            // This prevents interval format in legend for FIXED, GRADIENT, CONSTANT
            if (
                this.dataset.climatologyStylingOptions.type !==
                ColorScaleType.INTERVAL
            ) {
                this.dataset.climatologyStylingOptions.intervalOptions = [];
            }

            // Copy to renderingOptions for backward compatibility
            this.renderingOptions.datasetStylingOptions =
                this.dataset.climatologyStylingOptions;
        }

        // Ensure visualization options are set
        if (!this.renderingOptions.visualizationOptions) {
            this.renderingOptions.visualizationOptions = {
                type: VisualizationType.DEFAULT,
            };
        }

        // Set up converter options for legend generation
        if (this.selectedFieldForColorization && this.dataset) {
            this.renderingOptions.converterOptions = {
                type: this.getConverterType(this.selectedFieldForColorization),
                datasetID: this.dataset.id,
                fieldID: this.selectedFieldForColorization.id,
            };

            // Add field statistics if available
            if (
                this.selectedFieldForColorization.type ===
                DatasetFieldSpecificType.NUMBER_FIELD
            ) {
                // For number fields, we would need min/max from field statistics
                // These will be populated by the parent component
                this.renderingOptions.converterOptions.minNumber =
                    this.selectedFieldForColorization.min;
                this.renderingOptions.converterOptions.maxNumber =
                    this.selectedFieldForColorization.max;
            } else if (
                this.selectedFieldForColorization.type ===
                DatasetFieldSpecificType.DATE_FIELD
            ) {
                // For date fields
                this.renderingOptions.converterOptions.minNumber =
                    this.selectedFieldForColorization.min;
                this.renderingOptions.converterOptions.maxNumber =
                    this.selectedFieldForColorization.max;
            } else {
                // For text fields, values will be populated by parent
                this.renderingOptions.converterOptions.values = [];
            }
        }
        this.submitChanges.emit(this.renderingOptions);
    }

    onReset(): void {
        if (!this.dataset) {
            return;
        }

        this.renderingOptions.climatologyOptions =
            getDefaultClimatologyOptions();
        this.dataset.climatologyStylingOptions = {
            type: ColorScaleType.GRADIENT,
            colorizationFieldIndex: 0,
            defaultColor: 0xd3d3d3,
            colors: [0xfff2cc, 0xffd966, 0xf4cccc, 0xe06666, 0xcc0000], // [0x2166ac, 0x92c5de, 0xfddbc7, 0xf4a582, 0xb2182b],
            backgroundOpacity: 1,
            intervalOptions: [],
        };
        this.base64Colors = [
            "#fff2cc",
            "#ffd966",
            "#f4cccc",
            "#e06666",
            "#cc0000",
        ]; // ['#2166ac', '#92c5de', '#fddbc7', '#f4a582', '#b2182b'];
        this.defaultColor = "#d3d3d3";
        this.selectedFieldForColorization = null;
        this.intervalForm.reset({
            color: "#aaaaaa",
            minValue: null,
            maxValue: null,
        });
        this.intervalForm.controls.minValue.enable();
        this.colorInterval = "#aaaaaa";
        this.initializeForm();
        // After initializeForm, which rebuilds the form: a reset while on Consolidated Risk Score
        // puts its own fixed scale back rather than the climate one, otherwise the panel would be
        // left offering five colours for a 0-5 range.
        if (this.selectedScoreType === SCORE_TYPE.CONSOLIDATED_RISK_SCORE) {
            this.applyConsolidatedRiskScoreScale();
        }
        this.Reset.emit();
    }

    toggleColorizeMarkersPanel(): void {
        this.closePanel.emit();
    }

    onCancelAndReset(): void {
        if (!this.dataset || !this.dataset.id) {
            this.notifService.error("Cannot reset - dataset not available");
            this.closePanel.emit();
            return;
        }

        // Reset the markers to default colors first
        this.onReset();

        this.closePanel.emit();
    }

    onScoreTypeChanged(type: ScoreType): void {
        this.selectedScoreType = type;
        if (type === SCORE_TYPE.CONSOLIDATED_RISK_SCORE) {
            this.applyConsolidatedRiskScoreScale();
        } else {
            // Switching back off Consolidated Risk Score: put Risk Score's own colours back in the
            // panel. Left alone, the six CRS swatches (including its grey) and FIXED type would
            // stay shown under Risk Score, and a Risk Score Apply that followed would save them.
            this.populateColorsFromClimatologyStylingOptions();
            this.colorizationForm.controls.colorScaleType.setValue(null);
        }
    }

    /**
     * Installs the fixed Consolidated Risk Score scale into the panel's own
     * working state. The scale type is part of it: only FIXED maps one colour per score, so a
     * score of 0 can be its own grey rather than a blend of the two colours either side of it.
     *
     * Deliberately does not touch dataset.climatologyStylingOptions here - that object is shared
     * with Risk Score, and selecting this radio is not yet a commitment. onConsolidatedApply
     * writes the type and default colour into it, the same moment it writes the colours.
     */
    private applyConsolidatedRiskScoreScale(): void {
        this.base64Colors = [...CONSOLIDATED_RISK_SCORE_COLORS];
        this.defaultColor = CONSOLIDATED_RISK_SCORE_DEFAULT_COLOR;
        this.colorizationForm.controls.colorScaleType.setValue(
            ColorScaleType.FIXED,
        );
    }

    onConsolidatedApply(): void {
        // Mark all fields as touched to show validation errors
        Object.keys(this.colorizationForm.controls).forEach((key) => {
            this.colorizationForm.controls[key].markAsTouched();
        });

        const formValue = this.colorizationForm.value;

        // Validate time period, scenario
        if (!formValue.timePeriod) {
            this.notifService.error("Please select a Time Period");
            return;
        }
        const isHistoric = formValue.timePeriod === TimePeriod.HISTORIC;
        if (
            !isHistoric &&
            !formValue.scenario &&
            !this.colorizationForm.get("scenario").value
        ) {
            this.notifService.error("Please select a Scenario SSP");
            return;
        }
        if (!this.selectedVulnerabilityProfile) {
            this.notifService.error(
                "Please select a Vulnerability Profile Type",
            );
            return;
        }
        const selectedProfile = this.allVulnerabilityProfiles
            ? null
            : (this.selectedVulnerabilityProfile as Activity);

        // Update climatology options from form
        const selectedTimePeriod = formValue.timePeriod;
        const selectedScenario =
            selectedTimePeriod === TimePeriod.HISTORIC
                ? null
                : formValue.scenario ||
                  this.colorizationForm.get("scenario").value;

        this.renderingOptions.climatologyOptions = {
            enabled: true,
            selectedIndicator: null, // null for vulnerability-profile-based climatology
            timePeriod: selectedTimePeriod,
            scenario: selectedScenario,
            vulnerabilityProfileId: selectedProfile
                ? selectedProfile.id.toString()
                : ColorizeMarkersComponent.ALL_VULNERABILITY_PROFILES,
            vulnerabilityProfileName: selectedProfile
                ? selectedProfile.name
                : ColorizeMarkersComponent.ALL_VULNERABILITY_PROFILES_LABEL,
        };

        // Update climatology styling options with colors. Type and default colour are written
        // here rather than when the radio was selected, so choosing Consolidated Risk Score and
        // then backing out never touches the dataset the two score types share.
        if (this.dataset?.climatologyStylingOptions) {
            this.dataset.climatologyStylingOptions.type = ColorScaleType.FIXED;
            this.dataset.climatologyStylingOptions.defaultColor =
                ColorUtils.colorFromBase64(this.defaultColor);
            this.dataset.climatologyStylingOptions.colors =
                this.base64Colors.map((color) =>
                    ColorUtils.colorFromBase64(color),
                );
            this.dataset.climatologyStylingOptions.intervalOptions = [];

            // Copy to renderingOptions for backward compatibility
            this.renderingOptions.datasetStylingOptions =
                this.dataset.climatologyStylingOptions;
        }

        // Set consolidated risk score specific converter options
        if (this.renderingOptions.converterOptions) {
            this.renderingOptions.converterOptions.fieldID =
                SCORE_TYPE.CONSOLIDATED_RISK_SCORE;
            this.renderingOptions.converterOptions.datasetID = this.dataset?.id;
        }

        this.submitChanges.emit(this.renderingOptions);
    }

    // ========== INTERVAL MANAGEMENT ==========

    checkScaleTypeByStatisticalValues(): void {
        // Guard clauses
        if (!this.dataset?.id || !this.selectedFieldForColorization) {
            return;
        }

        const climatologyStyling = this.dataset.climatologyStylingOptions;

        // Skip validation for constant scale type
        if (climatologyStyling.type === ColorScaleType.CONSTANT) {
            return;
        }

        const filter: DatapointFilter = { datasetID: this.dataset.id };

        this.datapointAggregateService
            .getDatapointsFieldStatistics(
                this.dataset.id,
                this.selectedFieldForColorization.id,
                filter,
            )
            .subscribe((statistics) => {
                if (statistics.values) {
                    this.handleTextFieldStatistics(
                        statistics.values,
                        climatologyStyling,
                    );
                } else {
                    this.handleNumericFieldStatistics(
                        statistics,
                        climatologyStyling,
                    );
                }
            });
    }

    private handleTextFieldStatistics(
        values: any[],
        climatologyStyling: DatasetStylingOptions,
    ): void {
        const validValues = values.filter(Boolean);

        // No values available - fallback to constant
        if (validValues.length === 0) {
            this.notifService.error(
                "Not available Field values... CONSTANT Color will apply.",
            );
            climatologyStyling.type = ColorScaleType.CONSTANT;
            return;
        }

        const isFixedScale = climatologyStyling.type === ColorScaleType.FIXED;
        const isGradientScale =
            climatologyStyling.type === ColorScaleType.GRADIENT;

        // Too many colors for available values
        if (this.base64Colors.length > validValues.length && isFixedScale) {
            this.base64Colors.splice(
                validValues.length,
                this.base64Colors.length,
            );
            this.notifService.error(
                "Not allowing so many COLORS because of the available Field values",
            );
        }

        // Only one value with gradient scale
        if (validValues.length === 1 && isGradientScale) {
            this.notifService.error(
                "No GRADIENT could be generated with only one value.",
            );
        }

        // Multiple values but only one color
        if (validValues.length > 1 && this.base64Colors.length === 1) {
            this.base64Colors.push("#000000");
            this.notifService.error(
                "More COLOR is required to visualize this.",
            );
        }
    }

    private handleNumericFieldStatistics(
        statistics: any,
        climatologyStyling: DatasetStylingOptions,
    ): void {
        const { minValue, maxValue } = statistics;
        const hasValidRange = minValue != null && maxValue != null;

        if (!hasValidRange) {
            return;
        }

        // Single value case
        if (minValue === maxValue) {
            this.base64Colors = [this.base64Colors[0]];
            this.notifService.error(
                "Only one value available on this numeric field.",
            );
            return;
        }

        // Multiple values case
        const numberOfColorsAllowed = maxValue - minValue + 1;
        const isFixedScale = climatologyStyling.type === ColorScaleType.FIXED;
        const isGradientScale =
            climatologyStyling.type === ColorScaleType.GRADIENT;
        const requiresMultipleColors = isFixedScale || isGradientScale;

        // Too many colors for numeric range
        if (this.base64Colors.length > numberOfColorsAllowed && isFixedScale) {
            this.base64Colors.splice(
                numberOfColorsAllowed,
                this.base64Colors.length,
            );
            this.notifService.error(
                "Exceeded maximum number of COLORS allowed.",
            );
        }

        // Only one color but multiple values
        if (this.base64Colors.length === 1 && requiresMultipleColors) {
            this.base64Colors.push("#000000");
            this.notifService.error(
                "More COLOR is required to visualize this.",
            );
        }
    }

    addInterval(): void {
        const minValue = this.intervalForm.controls.minValue.value;
        const maxValue = this.intervalForm.controls.maxValue.value;
        const color = this.intervalForm.controls.color.value;

        // Validate form inputs
        if (!this.intervalForm.valid) {
            this.notifService.error("Please fill all required fields.");
            return;
        }

        if (minValue > maxValue && maxValue !== null) {
            this.notifService.error("Min cannot be greater than max.");
            return;
        }

        const NUMBER_INPUT_MAX = 100000000000;
        if (minValue > NUMBER_INPUT_MAX || maxValue > NUMBER_INPUT_MAX) {
            this.notifService.error("Exceeded the maximum allowed.");
            return;
        }

        if (minValue === null && maxValue === null) {
            this.notifService.error("Please fill a min or a max value.");
            return;
        }

        const intervals =
            this.dataset.climatologyStylingOptions.intervalOptions;
        const lastInterval = intervals[intervals.length - 1];

        // Save original maxValue before modification for potential rollback
        const originalLastIntervalMaxValue = lastInterval
            ? lastInterval.maxValue
            : null;

        // Create new interval
        const newInterval: IntervalOption = {
            color: ColorUtils.colorFromBase64(color),
            minValue: minValue,
            maxValue: maxValue,
            total: maxValue,
        };

        // Update last interval's maxValue to link with new interval
        if (lastInterval) {
            lastInterval.maxValue = minValue;
        }

        // Add new interval
        intervals.push(newInterval);

        // Validate intervals if dataset is ready
        if (this.dataset.id !== null && this.selectedFieldForColorization) {
            // Temporarily sync climatologyStylingOptions to stylingOptions for validation
            const originalStylingOptions = this.dataset.stylingOptions;
            this.dataset.stylingOptions =
                this.dataset.climatologyStylingOptions;

            const invalidAttribute = checkIntervalsValidity(
                this.dataset,
                intervals,
            );

            // Restore original stylingOptions
            this.dataset.stylingOptions = originalStylingOptions;

            if (invalidAttribute) {
                // Remove the invalid interval and restore last interval's original maxValue
                intervals.pop();
                if (lastInterval) {
                    lastInterval.maxValue = originalLastIntervalMaxValue;
                }

                switch (invalidAttribute) {
                    case RenderingErrorCodes.COLORS:
                        this.notifService.error(
                            "You must select some colors for the colorization",
                        );
                        break;
                    case RenderingErrorCodes.FIELD:
                        this.notifService.error(
                            "You must choose a field for the colorization",
                        );
                        break;
                    case RenderingErrorCodes.INTERVALS:
                        this.notifService.error(
                            "You must define at least one interval",
                        );
                        break;
                    case RenderingErrorCodes.INTERVALS_OVERLAP:
                        this.notifService.error(
                            "The defined intervals must not overlap",
                        );
                        break;
                    case RenderingErrorCodes.DUPLICATE_INTERVAL:
                        this.notifService.error(
                            "The defined intervals must not have duplicates",
                        );
                        break;
                }
                return;
            }
        }

        // Successfully added interval - update form for next interval
        if (maxValue !== null) {
            this.intervalForm.controls.minValue.setValue(maxValue);
            this.intervalForm.controls.minValue.disable();
        } else {
            this.intervalForm.controls.minValue.setValue(null);
            this.intervalForm.controls.minValue.enable();
        }

        // Reset form fields for next interval
        this.intervalForm.controls.maxValue.setValue(null);
        this.intervalForm.controls.color.setValue("#aaaaaa");
        this.colorInterval = "#aaaaaa";
    }

    onRemoveColorInterval(index: number): void {
        this.dataset.climatologyStylingOptions.intervalOptions.splice(index, 1);
        if (
            this.dataset.climatologyStylingOptions.intervalOptions[
                index - 1
            ] !== undefined
        ) {
            this.intervalForm.controls.minValue.setValue(
                this.dataset.climatologyStylingOptions.intervalOptions[
                    index - 1
                ].maxValue,
            );
        } else {
            this.intervalForm.controls.minValue.setValue(null);
            this.intervalForm.controls.minValue.enable();
        }
        this.intervalForm.controls.maxValue.setValue(null);
    }

    onColorPickerIntervalChange(newColor: string): void {
        this.colorInterval = newColor;
        this.intervalForm.get("color").setValue(newColor, { emitEvent: false });
    }

    private getConverterType(field: DatasetField): DatapointConverterType {
        switch (field.type) {
            case DatasetFieldSpecificType.NUMBER_FIELD:
                return DatapointConverterType.NUMBER_FIELD;
            case DatasetFieldSpecificType.DATE_FIELD:
                return DatapointConverterType.DATE_FIELD;
            default:
                return DatapointConverterType.TEXT_FIELD;
        }
    }
}
